import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Lock, AlertTriangle, Download, Sparkles } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, Legend
} from 'recharts';
import { TrialPaywallDialog } from '@/components/TrialPaywallDialog';
import { SlicerControl } from '@/components/report-builder/SlicerControl';
import { getSlicerSetting, sortSlicerValues, resolveDefaultSelection } from '@/lib/slicerSettings';
import { filtersForSelection } from '@/lib/visualQueryEngine';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const COLORS = ['#3b82f6', '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];
const TT = { background: 'rgba(9,9,11,0.9)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, fontSize: 11, color: '#e2e8f0' };

const OBJECT_VIZ = new Set(['ai_text', 'rich_text', 'image', 'shape']);

// The data-bearing visuals on the report's first page, in the shape the embed
// grid renders. Excludes free-form object visuals (text/image/shape).
function reportDataVisuals(cfg) {
  if (!cfg) return [];
  let all = cfg.visuals;
  if (!all && cfg.pageVisuals) all = Object.values(cfg.pageVisuals).flat().filter(Boolean);
  all = all || [];
  if (cfg.pages && cfg.pages.length > 1 && cfg.pageVisuals) {
    const firstPageId = cfg.pages[0]?.id;
    all = cfg.pageVisuals[firstPageId] || all;
  }
  return all.filter(v => v && v.id && !OBJECT_VIZ.has(v.vizType)
    && ((v.rowDims || []).length || (v.valueMeasures || []).length || (v.sqlMode && v.sqlData)));
}

// Shared viz body renderer — used by both the single-visual report and each
// cell of a multi-visual embedded dashboard. `height` adapts to the grid cell.
function renderVizBody({ vizType, data, xKey, vKeys, keys, height = 360 }) {
  if (!data || data.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-12">No data available</p>;
  }
  if (vizType === 'bar_chart') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis dataKey={xKey} tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false} />
          <YAxis tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false} />
          <ReTooltip contentStyle={TT} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {vKeys.map((k, i) => <Bar key={k} dataKey={k} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} />)}
        </BarChart>
      </ResponsiveContainer>
    );
  }
  if (vizType === 'line_chart') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis dataKey={xKey} tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false} />
          <YAxis tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false} />
          <ReTooltip contentStyle={TT} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {vKeys.map((k, i) => <Line key={k} type="monotone" dataKey={k} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />)}
        </LineChart>
      </ResponsiveContainer>
    );
  }
  if (vizType === 'pie_chart' || vizType === 'donut_chart') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={height > 220 ? 50 : 30} outerRadius={height > 220 ? 100 : 70} dataKey={vKeys[0] || 'value'} nameKey={xKey} paddingAngle={3} strokeWidth={0}>
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <ReTooltip contentStyle={TT} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
        </PieChart>
      </ResponsiveContainer>
    );
  }
  if (vizType === 'area_chart') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis dataKey={xKey} tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false} />
          <YAxis tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false} />
          <ReTooltip contentStyle={TT} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {vKeys.map((k, i) => <Area key={k} type="monotone" dataKey={k} stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.2} />)}
        </AreaChart>
      </ResponsiveContainer>
    );
  }
  if (vizType === 'kpi') {
    const vk = vKeys[0] || keys[0];
    const val = data[0]?.[vk];
    return (
      <div className="flex flex-col items-center justify-center h-full py-6">
        <p className="text-3xl font-heading font-bold text-foreground">
          {typeof val === 'number' ? val.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(val ?? '--')}
        </p>
      </div>
    );
  }
  // table / pivot / fallback
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="border-b border-border/50">{keys.map(k => <th key={k} className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase">{k.replace(/_/g, ' ')}</th>)}</tr></thead>
        <tbody>{data.slice(0, 200).map((row, i) => (
          <tr key={i} className="border-b border-border/20 hover:bg-muted/10">
            {keys.map(k => <td key={k} className="px-3 py-2 font-mono text-foreground/80">{typeof row[k] === 'number' ? row[k].toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(row[k] ?? '')}</td>)}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

async function publicRequest(path) {
  const res = await fetch(`${API}${path}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
  return data;
}

// Token-driven embed theme: applies CSS variable overrides to the whole
// embedded frame based on the theme claim baked into the token. Only an
// allow-listed set of variables is set; arbitrary CSS is never accepted.
function EmbedThemeWrapper({ theme = 'dark', brandTheme = {}, children }) {
  const vars = {};
  if (theme === 'light') {
    Object.assign(vars, {
      '--background': '0 0% 100%',
      '--foreground': '222 47% 11%',
      '--muted': '210 40% 96%',
      '--muted-foreground': '215 16% 47%',
      '--card': '0 0% 100%',
      '--card-foreground': '222 47% 11%',
      '--border': '214 32% 91%',
      '--input': '214 32% 91%',
      '--glass-bg': 'rgba(255,255,255,0.7)',
      'colorScheme': 'light',
    });
  } else if (theme === 'brand') {
    // Brand palette uses raw hex values as CSS variables consumed by
    // scoped classes below. Only the sanitized keys returned by the API
    // are honoured.
    if (brandTheme?.accent) vars['--embed-brand-accent'] = brandTheme.accent;
    if (brandTheme?.background) vars['--embed-brand-bg'] = brandTheme.background;
    if (brandTheme?.foreground) vars['--embed-brand-fg'] = brandTheme.foreground;
    if (brandTheme?.muted) vars['--embed-brand-muted'] = brandTheme.muted;
    if (brandTheme?.border) vars['--embed-brand-border'] = brandTheme.border;
  }
  return (
    <div
      className={`inzara-embed-theme inzara-embed-theme-${theme}`}
      data-testid={`embed-theme-${theme}`}
      data-embed-theme={theme}
      style={{ ...vars, minHeight: '100vh' }}
    >
      <style>{`
        .inzara-embed-theme-brand {
          background: var(--embed-brand-bg, #0b1220);
          color: var(--embed-brand-fg, #e2e8f0);
        }
        .inzara-embed-theme-brand .glass-card {
          background: color-mix(in srgb, var(--embed-brand-bg, #0b1220) 92%, transparent);
          border-color: var(--embed-brand-border, rgba(255,255,255,0.08));
        }
        .inzara-embed-theme-brand a,
        .inzara-embed-theme-brand .text-primary {
          color: var(--embed-brand-accent, #3b82f6);
        }
        .inzara-embed-theme-brand .bg-primary {
          background-color: var(--embed-brand-accent, #3b82f6) !important;
        }
        .inzara-embed-theme-brand .border-primary\\/30,
        .inzara-embed-theme-brand .border-primary\\/20 {
          border-color: color-mix(in srgb, var(--embed-brand-accent, #3b82f6) 30%, transparent);
        }
        .inzara-embed-theme-light .text-foreground { color: hsl(222 47% 11%); }
        .inzara-embed-theme-light .text-muted-foreground { color: hsl(215 16% 47%); }
        .inzara-embed-theme-light .bg-background { background: #ffffff; }
        .inzara-embed-theme-light .glass-card {
          background: rgba(255,255,255,0.8);
          border-color: rgba(15,23,42,0.08);
          box-shadow: 0 1px 3px rgba(15,23,42,0.06);
        }
      `}</style>
      {children}
    </div>
  );
}

export default function EmbedPage() {
  const { type, id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const isShare = searchParams.get('share') === '1';
  const themeOverride = searchParams.get('theme') || '';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resource, setResource] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [embedSel, setEmbedSel] = useState({});       // {dim: selected value}
  const [embedVals, setEmbedVals] = useState({});     // {dim: [available values]}
  const [visualData, setVisualData] = useState({});   // {visualId: {data, row_count}}
  const [dashData, setDashData] = useState({});
  const [shareInfo, setShareInfo] = useState(null); // { share_id, label, expires_at }
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Token-driven theme: { theme: 'light'|'dark'|'brand', brand_theme: {...} }
  const [embedTheme, setEmbedTheme] = useState({ theme: 'dark', brand_theme: {} });
  const [systemDark, setSystemDark] = useState(
    () => typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-color-scheme: dark)').matches : true
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setSystemDark(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange); };
  }, []);
  // 'auto' matches the viewer's device preference; explicit themes are honored.
  const effectiveTheme = embedTheme.theme === 'auto' ? (systemDark ? 'dark' : 'light') : embedTheme.theme;
  const [brandInfo, setBrandInfo] = useState({});
  useEffect(() => {
    if (brandInfo.app_name) {
      document.title = resource?.name ? `${resource.name} · ${brandInfo.app_name}` : brandInfo.app_name;
    }
  }, [brandInfo, resource]);

  // Fetch each visual's data by id (server resolves the visual + its query),
  // applying the viewer's current slicer filters to every visual.
  const fetchAllVisuals = async (vlist, filters) => {
    const out = {};
    await Promise.all((vlist || []).map(async (vis) => {
      try {
        const r = await fetch(`${API}/embed/report/${id}/execute`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, visual_id: vis.id, filters }),
        });
        if (r.ok) out[vis.id] = await r.json();
      } catch { /* per-visual failure shouldn't blank the whole page */ }
    }));
    setVisualData(prev => ({ ...prev, ...out }));
  };

  useEffect(() => {
    if (!token || !type || !id) { setError('Missing embed parameters'); setLoading(false); return; }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const v = await publicRequest(`/embed/validate?token=${encodeURIComponent(token)}&resource_type=${type}&resource_id=${id}`);
        if (v?.is_share_preview) {
          setShareInfo({ share_id: v.embed_id, label: v.label, expires_at: v.expires_at });
        }
        setBrandInfo(v?.brand_info || {});
        const bi = v?.brand_info || {};
        if (bi.favicon_url) {
          let link = document.querySelector("link[rel='icon']");
          if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
          link.href = bi.favicon_url;
        }
        // Resolve the theme: URL override wins for host-app preview,
        // but only if it maps to an allowed value. Otherwise the token's
        // theme claim is authoritative.
        const allowed = new Set(['light', 'dark', 'brand', 'auto']);
        const resolvedTheme = allowed.has(themeOverride) ? themeOverride : (v?.theme || 'auto');
        setEmbedTheme({
          theme: resolvedTheme,
          brand_theme: v?.brand_theme || {},
        });
        if (type === 'dashboard') {
          const dash = await publicRequest(`/embed/dashboard/${id}?token=${encodeURIComponent(token)}`);
          setResource(dash);
          if (dash?._embed_theme) {
            setEmbedTheme({
              theme: allowed.has(themeOverride) ? themeOverride : (dash._embed_theme.theme || 'dark'),
              brand_theme: dash._embed_theme.brand_theme || {},
            });
          }
          // Load KPIs and chart data via public data endpoints (these need the embed token)
          try {
            const kpis = await fetch(`${API}/data/kpis`, { headers: { 'Authorization': `EmbedToken ${token}` } }).then(r => r.ok ? r.json() : {});
            setDashData(prev => ({ ...prev, kpis }));
          } catch {}
        } else if (type === 'report') {
          const report = await publicRequest(`/embed/report/${id}?token=${encodeURIComponent(token)}`);
          setResource(report);
          if (report?._embed_theme) {
            setEmbedTheme({
              theme: allowed.has(themeOverride) ? themeOverride : (report._embed_theme.theme || 'dark'),
              brand_theme: report._embed_theme.brand_theme || {},
            });
          }
          try {
            const res = await fetch(`${API}/embed/report/${id}/execute`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token })
            });
            if (res.ok) setReportData(await res.json());
          } catch {}
          // Populate interactive slicers (authored filter dims) and apply their
          // configured default selection. Public embed has no viewer profile, so
          // profile-based defaults fall back to All/None.
          try {
            const cfg = report?.config || {};
            const settings = cfg.slicerSettings || {};
            const dims = [...new Set([...(cfg.globalFilterDims || []), ...((cfg.visuals || []).flatMap(v => v.filterDims || []))])];
            const initSel = {};
            for (const dim of dims) {
              try {
                const vr = await fetch(`${API}/embed/report/${id}/execute`, {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ token, values_for: dim })
                }).then(r => r.ok ? r.json() : { values: [] });
                const vals = vr.values || [];
                setEmbedVals(prev => ({ ...prev, [dim]: vals }));
                const st = getSlicerSetting(settings, dim);
                const def = resolveDefaultSelection({ setting: st, values: vals, isDate: false, reportId: id, dim });
                if (def != null) initSel[dim] = def;
              } catch {}
            }
            if (Object.keys(initSel).length) {
              setEmbedSel(initSel);
              const f = Object.entries(initSel).flatMap(([d, v]) => filtersForSelection(d, v));
              if (f.length) {
                const r2 = await fetch(`${API}/embed/report/${id}/execute`, {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ token, filters: f })
                });
                if (r2.ok) setReportData(await r2.json());
              }
            }
            // Multi-visual dashboards: fetch every visual's data so the embed
            // renders the full page exactly as laid out (slicers filter all).
            const dataVis = reportDataVisuals(cfg);
            if (dataVis.length >= 2) {
              const initFilters = Object.entries(initSel).flatMap(([d, v]) => filtersForSelection(d, v));
              await fetchAllVisuals(dataVis, initFilters);
            }
          } catch {}
        }
      } catch (err) { setError(err.message); }
      finally { setLoading(false); }
    })();
  }, [token, type, id, themeOverride, reloadKey]);

  // SDK auto-refresh support — the parent frame posts
  // {type: 'inzara.embed.refresh'} to trigger a re-fetch. We accept the
  // signal only when embedded inside another window.
  useEffect(() => {
    const onRefreshMsg = (event) => {
      if (!event?.data || typeof event.data !== 'object') return;
      if (event.data.type !== 'inzara.embed.refresh') return;
      if (window.parent === window) return;
      setReloadKey((k) => k + 1);
    };
    window.addEventListener('message', onRefreshMsg);
    return () => window.removeEventListener('message', onRefreshMsg);
  }, []);

  // Iter C SDK v1 — post 'ready' handshake once loading finishes so host
  // apps embedding via <InzaraDashboard> get a reliable ready signal.
  useEffect(() => {
    if (loading) return;
    try {
      if (window.parent && window.parent !== window) {
        if (error) {
          window.parent.postMessage({ type: 'inzara.embed.error', message: error }, '*');
        } else {
          window.parent.postMessage({
            type: 'inzara.embed.ready',
            resource_type: type,
            resource_id: id,
            embed_id: shareInfo?.share_id,
          }, '*');
        }
      }
    } catch (e) { /* embedded frame not accessible — no-op */ }
  }, [loading, error, type, id, shareInfo]);

  if (loading) {
    return (
      <EmbedThemeWrapper theme={effectiveTheme} brandTheme={embedTheme.brand_theme}>
        <div className="min-h-screen bg-background flex items-center justify-center" data-testid="embed-loading">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <p className="text-sm text-muted-foreground">Loading embedded content...</p>
          </div>
        </div>
      </EmbedThemeWrapper>
    );
  }

  if (error) {
    return (
      <EmbedThemeWrapper theme={effectiveTheme} brandTheme={embedTheme.brand_theme}>
        <div className="min-h-screen bg-background flex items-center justify-center" data-testid="embed-error">
          <div className="glass-card rounded-2xl p-8 max-w-md text-center">
            <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/20 w-fit mx-auto mb-4">
              {error.includes('expired') ? <AlertTriangle className="w-6 h-6 text-amber-400" /> : <Lock className="w-6 h-6 text-destructive" />}
            </div>
            <h2 className="text-lg font-heading font-bold text-foreground mb-2">
              {error.includes('expired') ? 'Embed Link Expired' : 'Access Denied'}
            </h2>
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        </div>
      </EmbedThemeWrapper>
    );
  }

  // ─── Embedded Dashboard ───
  if (type === 'dashboard' && resource) {
    return (
      <EmbedThemeWrapper theme={effectiveTheme} brandTheme={embedTheme.brand_theme}>
      <div className="min-h-screen bg-background p-6" data-testid="embed-dashboard">
        {isShare && (
          <div className="max-w-7xl mx-auto mb-4 p-3 rounded-xl border border-primary/30 bg-primary/5 flex items-center justify-between gap-3" data-testid="embed-share-banner">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
              <p className="text-xs text-foreground/90 truncate">
                You're viewing a <span className="font-semibold">free preview</span>. Want this dashboard for your own data?
              </p>
            </div>
            <Button size="sm" className="rounded-lg text-[11px] h-7 bg-primary text-primary-foreground flex-shrink-0" onClick={() => setPaywallOpen(true)} data-testid="embed-claim-trial-btn">
              Start 14-day trial
            </Button>
          </div>
        )}
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">{resource.name}</h1>
              {resource.description && <p className="text-sm text-muted-foreground mt-1">{resource.description}</p>}
            </div>
            <div className="flex items-center gap-2">
              {isShare && (
                <Button
                  size="sm"
                  className="rounded-lg text-xs h-8 gap-1.5 bg-primary text-primary-foreground"
                  onClick={() => setPaywallOpen(true)}
                  data-testid="embed-download-pdf-btn"
                >
                  <Download className="w-3 h-3" /> Download PDF
                </Button>
              )}
              <Badge className="text-[9px] bg-primary/10 text-primary border-primary/20">
                {isShare ? 'Public Preview' : 'Embedded View'}
              </Badge>
            </div>
          </div>
          {/* KPI Cards */}
          {resource.widgets?.filter(w => w.widget_type === 'kpi').length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {resource.widgets.filter(w => w.widget_type === 'kpi').map(w => (
                <div key={w.widget_id} className="glass-card rounded-2xl p-5" data-testid={`embed-kpi-${w.widget_id}`}>
                  <p className="text-sm text-muted-foreground">{w.title}</p>
                  <p className="text-2xl font-heading font-bold text-foreground mt-1">{w.config?.value || w.config?.trend || '--'}</p>
                  {w.config?.trend && <p className="text-xs text-emerald-400 mt-1">{w.config.trend}</p>}
                </div>
              ))}
            </div>
          )}
          {/* Chart Widgets */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {resource.widgets?.filter(w => w.widget_type !== 'kpi').map(w => (
              <div key={w.widget_id} className="glass-card rounded-2xl p-5" data-testid={`embed-widget-${w.widget_id}`}>
                <p className="text-sm font-heading font-semibold text-foreground mb-3">{w.title}</p>
                <div className="h-48 flex items-center justify-center text-xs text-muted-foreground">
                  <p>Chart: {w.widget_type.replace(/_/g, ' ')}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <div className="flex items-center justify-center gap-1.5 opacity-40">
              {brandInfo.logo_url && <img src={brandInfo.logo_url} alt={brandInfo.app_name || 'INZARA'} className="h-4 object-contain" />}
              <p className="text-[10px] text-muted-foreground">Powered by {brandInfo.app_name || 'INZARA'}</p>
            </div>
          </div>
        </div>
      </div>
      <TrialPaywallDialog open={paywallOpen} onOpenChange={setPaywallOpen} shareId={shareInfo?.share_id} resourceName={resource?.name} brand={brandInfo} />
      </EmbedThemeWrapper>
    );
  }

  // ─── Embedded Report ───
  if (type === 'report' && resource) {
    const qdef = resource.query_definition || {};
    const data = reportData?.data || [];
    const vizType = resource.visualization_type || resource.config?.visuals?.[0]?.vizType || 'table';
    const keys = data.length > 0 ? Object.keys(data[0]) : [];
    const dims = qdef.dimensions || [];
    const xKey = dims[0] || keys[0];
    const vKeys = keys.filter(k => !dims.includes(k));

    const cfgR = resource.config || {};
    const dataVis = reportDataVisuals(cfgR);
    const isMulti = dataVis.length >= 2;
    const gridLayout = Object.fromEntries((cfgR.gridLayouts?.lg || []).map(l => [l.i, l]));

    return (
      <EmbedThemeWrapper theme={effectiveTheme} brandTheme={embedTheme.brand_theme}>
      <div className="min-h-screen bg-background p-6" data-testid="embed-report">
        {isShare && (
          <div className={`${isMulti ? 'max-w-7xl' : 'max-w-5xl'} mx-auto mb-4 p-3 rounded-xl border border-primary/30 bg-primary/5 flex items-center justify-between gap-3`} data-testid="embed-share-banner">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
              <p className="text-xs text-foreground/90 truncate">
                You're viewing a <span className="font-semibold">free preview</span>. Want this dashboard for your own data?
              </p>
            </div>
            <Button size="sm" className="rounded-lg text-[11px] h-7 bg-primary text-primary-foreground flex-shrink-0" onClick={() => setPaywallOpen(true)} data-testid="embed-claim-trial-btn">
              Start 14-day trial
            </Button>
          </div>
        )}
        <div className={`${isMulti ? 'max-w-7xl' : 'max-w-5xl'} mx-auto`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">{resource.name}</h1>
              {resource.description && <p className="text-sm text-muted-foreground mt-1">{resource.description}</p>}
            </div>
            <div className="flex items-center gap-2">
              {reportData && <span className="text-[10px] text-muted-foreground">{reportData.row_count} rows</span>}
              {isShare && (
                <Button
                  size="sm"
                  className="rounded-lg text-xs h-8 gap-1.5 bg-primary text-primary-foreground"
                  onClick={() => setPaywallOpen(true)}
                  data-testid="embed-download-pdf-btn"
                >
                  <Download className="w-3 h-3" /> Download PDF
                </Button>
              )}
              <Badge className="text-[9px] bg-primary/10 text-primary border-primary/20">
                {isShare ? 'Public Preview' : 'Embedded View'}
              </Badge>
            </div>
          </div>
          {(() => {
            const cfg = resource.config || {};
            const settings = cfg.slicerSettings || {};
            const slicerDims = [...new Set([...(cfg.globalFilterDims || []), ...((cfg.visuals || []).flatMap(v => v.filterDims || []))])];
            if (!slicerDims.length) return null;
            const onChange = async (dim, val) => {
              const next = { ...embedSel };
              if (val == null || (Array.isArray(val) && !val.length)) delete next[dim]; else next[dim] = val;
              setEmbedSel(next);
              const f = Object.entries(next).flatMap(([d, v]) => filtersForSelection(d, v));
              if (isMulti) { fetchAllVisuals(dataVis, f); return; }
              try {
                const r = await fetch(`${API}/embed/report/${id}/execute`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, filters: f }) });
                if (r.ok) setReportData(await r.json());
              } catch {}
            };
            return (
              <div className="flex flex-wrap gap-2 mb-4" data-testid="embed-slicer-bar">
                {slicerDims.map(dim => (
                  <SlicerControl key={dim} dimension={dim} values={sortSlicerValues(embedVals[dim] || [], getSlicerSetting(settings, dim).sortOrder)} selected={embedSel[dim]} onChange={(v) => onChange(dim, v)} isDate={false} settings={getSlicerSetting(settings, dim)} />
                ))}
              </div>
            );
          })()}
          {isMulti ? (
            <div
              className="grid gap-4"
              style={{ gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gridAutoRows: '88px', gridAutoFlow: 'dense' }}
              data-testid="embed-visual-grid"
            >
              {dataVis.map((vis, idx) => {
                const vd = visualData[vis.id]?.data || [];
                const vkeys = vd.length > 0 ? Object.keys(vd[0]) : [];
                const vdims = [...(vis.rowDims || []), ...(vis.colDim ? [vis.colDim] : [])];
                const vxKey = (vis.rowDims || [])[0] || vkeys[0];
                const vvKeys = (vis.valueMeasures || []).filter(m => vkeys.includes(m));
                const effVKeys = vvKeys.length ? vvKeys : vkeys.filter(k => !vdims.includes(k));
                const gl = gridLayout[vis.id];
                const cellStyle = gl
                  ? { gridColumn: `${(gl.x || 0) + 1} / span ${gl.w || 6}`, gridRow: `${(gl.y || 0) + 1} / span ${gl.h || 4}` }
                  : { gridColumn: 'span 6', gridRow: 'span 4' };
                const cellH = Math.max(140, ((gl?.h || 4) * 88) + ((gl?.h || 4) - 1) * 16 - 64);
                return (
                  <div key={vis.id} className="glass-card rounded-2xl p-4 overflow-hidden flex flex-col" style={cellStyle} data-testid={`embed-visual-${vis.id}`}>
                    {vis.title && <p className="text-xs font-heading font-semibold text-foreground mb-2 truncate flex-shrink-0">{vis.title}</p>}
                    <div className="flex-1 min-h-0 overflow-auto">
                      {renderVizBody({ vizType: vis.vizType, data: vd, xKey: vxKey, vKeys: effVKeys, keys: vkeys, height: cellH })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
          <div className="glass-card rounded-2xl p-6">
            {renderVizBody({ vizType: (vizType === 'table' || vizType === 'pivot') ? 'table' : vizType, data, xKey, vKeys, keys, height: 360 })}
          </div>
          )}
          <div className="mt-8 text-center">
            <div className="flex items-center justify-center gap-1.5 opacity-40">
              {brandInfo.logo_url && <img src={brandInfo.logo_url} alt={brandInfo.app_name || 'INZARA'} className="h-4 object-contain" />}
              <p className="text-[10px] text-muted-foreground">Powered by {brandInfo.app_name || 'INZARA'}</p>
            </div>
          </div>
        </div>
      </div>
      <TrialPaywallDialog open={paywallOpen} onOpenChange={setPaywallOpen} shareId={shareInfo?.share_id} resourceName={resource?.name} brand={brandInfo} />
      </EmbedThemeWrapper>
    );
  }

  return (
    <EmbedThemeWrapper theme={effectiveTheme} brandTheme={embedTheme.brand_theme}>
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Content not available</p>
      </div>
    </EmbedThemeWrapper>
  );
}
